/**
 * Pure helpers for building server-side contact queries.
 *
 * Dependency-free by design: no Supabase import here, so these helpers stay
 * trivially unit-testable and reusable from any query-building context.
 */

// ═══════════════════════════════════════════════════════════════════════════
// SEARCH TERM SANITIZATION
// ═══════════════════════════════════════════════════════════════════════════

// PostgREST's `or(...)` filter grammar treats `,` `(` `)` as structural
// separators, so an unescaped occurrence in a user search term breaks the
// filter expression entirely. `%` and `*` are ILIKE/glob wildcards that turn
// a literal search into an unintended pattern match, and `\` can escape the
// wildcard that follows it. Stripping all of these keeps the term literal.
const ILIKE_UNSAFE_CHARS = /[,()%*\\]/g;

export const sanitizeIlikeTerm = (term: string): string =>
  term.replace(ILIKE_UNSAFE_CHARS, ' ').replace(/\s+/g, ' ').trim();

// Escapes ILIKE pattern metacharacters (`%` `_` `\`) so a raw value can be
// safely interpolated into a `%${value}%` pattern without introducing
// unintended wildcards. This is a DIFFERENT concern from `sanitizeIlikeTerm`
// above: that strips PostgREST `.or()` grammar characters (`,()`) that break
// parsing; this escapes ILIKE's own wildcard semantics, which apply even to
// a value passed through a parameterized two-argument `.ilike()` call.
//
// `*` is STRIPPED here, not escaped, unlike `%`/`_` — PostgREST substitutes
// a literal `*` for `%` in the pattern BEFORE Postgres ever sees it, so an
// escaped `\*` would still arrive as a literal-`%` match instead of a
// neutralized one. Stripping is the only treatment that actually removes
// the wildcard, matching `sanitizeIlikeTerm`'s own treatment of `*` above
// (DIFF Round 3, F4).
export const escapeIlikePattern = (term: string): string =>
  term.replace(/\\/g, '\\\\').replace(/%/g, '\\%').replace(/_/g, '\\_').replace(/\*/g, '');

// ═══════════════════════════════════════════════════════════════════════════
// DUE-DATE WINDOW FILTERS
// ═══════════════════════════════════════════════════════════════════════════

export interface DueWindow {
  from?: string;
  to?: string;
  requiresDueDate: boolean;
}

const toIsoDate = (value: Date): string => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const startOfDay = (value: Date): Date => {
  const result = new Date(value);
  result.setHours(0, 0, 0, 0);
  return result;
};

/**
 * Returns ISO `YYYY-MM-DD` bounds for a *server-side superset filter* on
 * `due_date`, matching the bucket boundaries implemented by `getDueBucket` in
 * `src/lib/crm/dashboard.ts` (mirrored exactly, including the week-end
 * arithmetic: `endOfWeek = today + ((7 - today.getDay()) % 7)`, i.e. the week
 * ends on Sunday).
 *
 * These bounds exist only to let a query narrow down rows before they reach
 * the client — the authoritative dashboard lane bucketing (myTurn/theirTurn/
 * scheduled/nurture/onHold, and the overdue/today/thisWeek/later split
 * within myTurn) stays in `dashboard.ts`. Do NOT "optimise" by moving that
 * lane logic into SQL/PostgREST filters here; this function must remain a
 * pure superset filter.
 */
export const dueWindowFor = (
  filter: 'overdue' | 'due-today' | 'due-this-week' | null,
  now: Date,
): DueWindow | null => {
  if (!filter) return null;

  const today = startOfDay(now);
  const todayIso = toIsoDate(today);

  if (filter === 'overdue') {
    // Strictly before today: caller should apply this as an exclusive
    // upper bound (e.g. `due_date < to`).
    return { to: todayIso, requiresDueDate: true };
  }

  if (filter === 'due-today') {
    // Exactly today.
    return { from: todayIso, to: todayIso, requiresDueDate: true };
  }

  // 'due-this-week': today through end of week, inclusive.
  const endOfWeek = new Date(today);
  endOfWeek.setDate(today.getDate() + ((7 - today.getDay()) % 7));

  return { from: todayIso, to: toIsoDate(endOfWeek), requiresDueDate: true };
};
